<template>
  <div>
    <div class="uk-card-body dcs-section-header">1. Select Data Request</div>
    <div class="uk-card-body">
      <request-type-form
        :request-type.sync="requestType"
        :has-error="$v.requestType.$dirty && !$v.requestType.required"
        :law="law"
        :brand="brand"
        :appeal-type.sync="appealType"
        :previous-request-date.sync="previousRequestDate"
        :appeal-reason.sync="appealReason"
      />
      <div
        class="uk-text-danger"
        v-if="$v.requestType.$dirty && !$v.requestType.required"
      >
        You must select a request type.
      </div>
      <div
        class="uk-text-danger"
        v-if="$v.appealType.$dirty && !$v.appealType.requireIfAppeal"
      >
        You must select type of prior request
      </div>
      <div
        class="uk-text-danger"
        v-if="$v.previousRequestDate.$dirty && !$v.previousRequestDate.requireIfAppeal"
      >
        You must select date of prior request submission
      </div>

      <div
        class="uk-text-danger"
        v-if="$v.appealReason.$dirty && !$v.appealReason.requireIfAppeal"
      >
        You must input a description of your appeal
      </div>

    </div>
    <div class="uk-card-body dcs-section-header">
      2. Add Your Personal Information
    </div>
    <div class="uk-card-body">
      <p>
        <content-section content-key="add-your-personal-info" />
      </p>
      <div v-if="recaptchaFailedToLoad" class="dcs-alert uk-alert uk-alert-danger">
        Something went wrong loading reCAPTCHA, try refreshing.
      </div>
      <div v-if="errors != null" class="dcs-alert uk-alert uk-alert-danger">
        Some of the entered information was not able to process. Please review
        the following errors:
        <ul class="uk-list">
          <li v-for="(error, field) in errors" :key="field">
            {{ field }}: {{ error }}
          </li>
        </ul>
      </div>
      <form @submit.prevent="onSubmit">
        <div class="uk-grid">
          <div class="uk-width-1-1 uk-width-1-2@m">
            <label class="uk-form-label">First name</label>
            <div
              class="uk-form-controls"
              :class="{
                'dcs-has-error': $v.first_name.$dirty && $v.first_name.$invalid
              }"
            >
              <input
                name="first_name"
                v-model.trim="$v.first_name.$model"
                class="uk-input dcs-input"
              />

              <div
                class="uk-text-danger"
                v-if="$v.first_name.$dirty && !$v.first_name.required"
              >
                You must enter a first name.
              </div>
            </div>
          </div>

          <div class="uk-width-1-1 uk-width-1-2@m">
            <label class="uk-form-label">Last name</label>
            <div
              class="uk-form-controls"
              :class="{
                'dcs-has-error': $v.last_name.$dirty && $v.last_name.$invalid
              }"
            >
              <input
                name="last_name"
                v-model.trim="$v.last_name.$model"
                class="uk-input dcs-input"
              />

              <div
                class="uk-text-danger"
                v-if="$v.last_name.$dirty && !$v.last_name.required"
              >
                You must enter a last name.
              </div>
            </div>
          </div>

          <div class="uk-width-1-1 uk-width-1-2@m">
            <label class="uk-form-label">Social Security Number</label>
            <div
              class="uk-form-controls"
              :class="{ 'dcs-has-error': $v.ssn.$dirty && $v.ssn.$invalid }"
            >
              <input
                v-model="$v.ssn.$model"
                type="tel"
                name="ssn"
                v-mask="'###-##-####'"
                placeholder="111-11-1111"
                class="uk-input dcs-input dcs-input-lock"
              />

              <div
                class="uk-text-danger"
                v-if="$v.ssn.$dirty && !$v.ssn.required"
              >
                You must enter a Social Security Number for us to identify your
                account.
              </div>

              <div
                class="uk-text-danger"
                v-if="$v.ssn.$dirty && !$v.ssn.ssnFormat"
              >
                Please enter your Social Security Number with dashes, e.g.
                123-45-6789.
              </div>
            </div>
          </div>

          <div class="uk-width-1-1 uk-width-1-2@m">
            <label class="uk-form-label">Date of Birth</label>
            <div
              class="uk-form-controls"
              :class="{
                'dcs-has-error':
                  $v.date_of_birth.$dirty && $v.date_of_birth.$invalid
              }"
            >
              <input
                v-if="showNativeDatepicker"
                name="date_of_birth"
                v-model="date_of_birth"
                type="date"
                class="uk-input dcs-input"
              />

              <date-picker v-else @date-change="date_of_birth = $event" />

              <div
                class="uk-text-danger"
                v-if="$v.date_of_birth.$dirty && $v.date_of_birth.$invalid"
              >
                Please enter a valid birth date.
              </div>
            </div>
          </div>

          <div class="uk-width-1-1 uk-text-center">
            <div class="three-quarters-loader" v-if="submitting"></div>
            <input
              v-else
              type="submit"
              value="Continue"
              class="uk-width-1-1 uk-button uk-button-primary uk-button-large dcs-button"
              :disabled="recaptchaFailedToLoad"
            />
            <p class="uk-animation-fade">
              <small>
                This site is protected by reCAPTCHA and the Google
                <a href="https://policies.google.com/privacy">Privacy Policy</a> and
                <a href="https://policies.google.com/terms">Terms of Service</a> apply.
              </small>
            </p>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import ContentSection from "./elements/ContentSection";
import RequestTypeForm from "./RequestTypeForm";
import DatePicker from "./elements/DatePicker";
import { postWithCSRF } from "../utils/api";

import { required, helpers } from "vuelidate/lib/validators";

import * as sentry from "@sentry/vue";
import { datadogRum } from "@datadog/browser-rum";

const ssnFormat = helpers.regex("ssnFormat", /^\d{3,3}-\d{2,2}-\d{4,4}$/);

const requireIfAppeal = (value, vm) => {
    if (vm.requestType === 'appeal') {
      return !!value;
    }
    return true;
  }

export default {
  name: "identification-page",
  components: { ContentSection, RequestTypeForm, DatePicker },
  data: function() {
    return {
      first_name: "",
      last_name: null,
      ssn: null,
      date_of_birth: null,
      requestType: null,
      appealType: null,
      previousRequestDate: null,
      appealReason: null,
      errors: null,
      submitting: false,
      recaptchaToken: null,
      recaptchaFailedToLoad: false
    };
  },
  validations: {
    requestType: {
      required
    },
    first_name: {
      required
    },
    last_name: {
      required
    },
    ssn: {
      required,
      ssnFormat
    },
    date_of_birth: {
      required
    },
    appealType: {
      requireIfAppeal
    },
    previousRequestDate: {
      requireIfAppeal
    },
    appealReason: {
      requireIfAppeal
    }
  },
  props: {
    identificationUrl: String,
    law: String,
    brand: String
  },
  computed: {
    showNativeDatepicker: function() {
      const iOS =
        /iPad|iPhone|iPod/.test(this.$browserDetect.meta.ua) &&
        !window.MSStream;

      return !(
        this.$browserDetect.isIE ||
        (!iOS && this.$browserDetect.isSafari) ||
        this.$browserDetect.meta.ua.indexOf("Opera Mini") > -1
      );
    },
  },
  methods: {
    onSubmit: async function() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        return;
      }

      this.errors = null;
      this.submitting = true;

      try {
        await this.$recaptchaLoaded();
        this.recaptchaToken = await this.$recaptcha(this.law);
      } catch (error) {
        this.submitting = false;
        this.recaptchaFailedToLoad = true;
        datadogRum.addError(`reCAPTCHA error: ${error}`);
        return;
      }

      this.$emit("willSubmit");

      const payload = {
        law: this.$props.law,
        purpose: `data-${this.requestType}`,
        previous_request_type: this.appealType,
        previous_request_date: this.previousRequestDate,
        appeal_description: this.appealReason,
        first_name: this.first_name,
        last_name: this.last_name,
        ssn: this.ssn,
        date_of_birth: this.date_of_birth,
        recaptcha_response: this.recaptchaToken
      };
      postWithCSRF(this.$props.identificationUrl, payload)
        .then(response => {
          this.$emit("complete", response);
          datadogRum.addAction("DataCompliance.IdentificationPage.Submit.Success", { purpose: this.requestType });
          setTimeout(() => this.submitting = false, 100);
        })
        .catch(error => {
          sentry.captureMessage(`${error}`, { level: "error", extra: { stack: error.stack } });
          datadogRum.addAction("DataCompliance.IdentificationPage.Submit.Error", { error: `${error}`, stack: error.stack });

          if (
            error.response &&
            typeof error.response.body === Object &&
            "errors" in error.response.body
          ) {
            this.errors = {};
            error.response.body.errors.forEach(e => {
              this.errors[e.field] = e.error;
            });
          } else {
            this.$emit("error", "server-error");
          }

          this.submitting = false;
        });
    }
  }
};
</script>

<style scoped></style>
